import { GameEvents } from '../GameEvents';
import { getCampaignForLevel, getAllLevelIds } from '../levelManifest';
import getLogger from '../logger';
import type EventBus from '../EventBus';
import type { LevelDataService } from '../services/LevelDataService';

const logger = getLogger('GameplayHandler');

interface GameplaySlice {
  currentLevelKey: string | null;
  levelWinData: {
    currentMoveCount: number;
    currentUndoCount: number;
    bestPlayerMoveCount: number | null;
    bestSolutionMoveCount: number | null;
  } | null;
  actionModes: string[];
  actionModeStates: Record<string, unknown>;
  currentActionMode: string;
  currentMoveCount: number;
  currentUndoCount: number;
  lastCompletedLevelSolution: any[] | null;
  optimalMoveCounts: Record<string, number>;
}

export default class GameplayHandler {
  private state: any;
  private slice: GameplaySlice;
  private eventBus: EventBus;
  private dataService: LevelDataService;

  constructor(state: any, slice: GameplaySlice, eventBus: EventBus, dataService: LevelDataService) {
    this.state = state;
    this.slice = slice;
    this.eventBus = eventBus;
    this.dataService = dataService;

    // Pre-compute optimal move counts for all levels
    this._populateOptimalMoveCounts();

    // LEVEL_WON – does NOT consume (other handlers also need it)
    this.eventBus.on(GameEvents.LEVEL_WON, (payload: any) => this._onLevelWon(payload));
    // LEVEL_LOST – does NOT consume
    this.eventBus.on(GameEvents.LEVEL_LOST, () => this._onLevelLost());
    // RESTART_GAME – consumes (returns true)
    this.eventBus.on(GameEvents.RESTART_GAME, () => this._onRestartGame());
    this.eventBus.on(GameEvents.CONTINUE_CAMPAIGN, () => this._onContinueCampaign());
    this.eventBus.on(GameEvents.CONTINUE_TO_NEXT_LEVEL, () => this._onContinueToNextLevel());
    this.eventBus.on(GameEvents.REPLAY_CURRENT_LEVEL, () => this._onReplayCurrentLevel());
    this.eventBus.on(GameEvents.LEVEL_PREPARED, (payload: any) => this._onLevelPrepared(payload));
    this.eventBus.on(GameEvents.ACTION_MODES_UPDATED, (payload: any) => this._onActionModesUpdated(payload));
    this.eventBus.on(GameEvents.ACTION_MODE_CHANGED, (mode: string) => { this.slice.currentActionMode = mode; });
    this.eventBus.on(GameEvents.MOVE_COUNT_UPDATED, (payload: any) => this._onMoveCountUpdated(payload));
    this.eventBus.on(GameEvents.SHOW_VISION, (payload: any) => this._onShowVision(payload));
  }

  async _onLevelWon(payload?: { moveCount: number; undoCount?: number; playerSolution?: any[] }) {
    const completedLevelKey = this.slice.currentLevelKey;
    logger.info('Level Won!', completedLevelKey);

    this._clearActionModes();

    if (payload && completedLevelKey) {
      const bestSolutionMoveCount = this._getBestSolutionMoveCount(completedLevelKey);

      // ProgressHandler also listens to LEVEL_WON and updates levelStates (async).
      // Defer so its update is visible before we read bestPlayerMoveCount / campaign state.
      await new Promise<void>(resolve => setTimeout(resolve, 0));

      const bestPlayerMoveCount = this._getBestPlayerMoveCount(completedLevelKey);

      this.slice.levelWinData = {
        currentMoveCount: payload.moveCount,
        currentUndoCount: payload.undoCount ?? 0,
        bestPlayerMoveCount,
        bestSolutionMoveCount,
      };

      // Store solution for replay access
      this.slice.lastCompletedLevelSolution = payload.playerSolution || null;
    }

    this.eventBus.emit(GameEvents.SET_SCREEN, 'level_win');
  }

  _onLevelLost() {
    logger.info('Level Lost!');
    this._clearActionModes();
    this.eventBus.emit(GameEvents.SET_SCREEN, 'lose');
  }

  _onRestartGame() {
    const levelKey = this.slice.currentLevelKey || this.state.progress.nextUnbeatenLevel;
    if (levelKey) {
      this.eventBus.emit(GameEvents.PLAY_LEVEL, { levelKey });
    } else {
      logger.warn('No level to restart');
      this.eventBus.emit(GameEvents.SET_SCREEN, 'home');
    }
    return true; // consume
  }

  _onContinueCampaign() {
    const nextLevel = this.state.progress.nextUnbeatenLevel;
    if (nextLevel) {
      this.eventBus.emit(GameEvents.PLAY_LEVEL, { levelKey: nextLevel });
    } else {
      logger.info('All levels completed!');
      this.eventBus.emit(GameEvents.SET_SCREEN, 'win');
    }
  }

  _onContinueToNextLevel() {
    const currentKey = this.slice.currentLevelKey;
    const nextLevelId = this.state.progress.nextUnbeatenLevel;

    // If next level is in the same campaign, continue playing
    if (nextLevelId && currentKey) {
      const currentCampaign = getCampaignForLevel(currentKey);
      const nextCampaign = getCampaignForLevel(nextLevelId);
      if (currentCampaign && nextCampaign && currentCampaign.id === nextCampaign.id) {
        this.eventBus.emit(GameEvents.PLAY_LEVEL, { levelKey: nextLevelId });
        return;
      }
    }

    // End of chapter or game
    this.slice.currentLevelKey = null;
    if (this.state.progress.gameComplete) {
      this.eventBus.emit(GameEvents.SET_SCREEN, 'win');
    } else {
      this.eventBus.emit(GameEvents.SET_SCREEN, 'home');
    }
  }

  _onReplayCurrentLevel() {
    if (this.slice.currentLevelKey) {
      this.eventBus.emit(GameEvents.PLAY_LEVEL, { levelKey: this.slice.currentLevelKey });
    } else {
      this.eventBus.emit(GameEvents.SET_SCREEN, 'home');
    }
  }

  _onLevelPrepared(payload: { levelKey: string }) {
    this.slice.currentLevelKey = payload.levelKey;
  }

  _onActionModesUpdated({ availableModes, modeStates }: { availableModes: string[]; modeStates: Record<string, unknown> }) {
    this.slice.actionModes = availableModes;
    this.slice.actionModeStates = modeStates;
  }

  _onMoveCountUpdated({ moveCount, undoCount }: { moveCount: number; undoCount?: number }) {
    this.slice.currentMoveCount = moveCount;
    this.slice.currentUndoCount = undoCount || 0;
  }

  _clearActionModes() {
    this.slice.actionModes = [];
    this.slice.actionModeStates = {};
    this.slice.currentActionMode = 'STANDARD';
  }

  _getBestPlayerMoveCount(levelKey: string): number | null {
    const ls = this.state.progress.levelStates[levelKey];
    if (!ls) return null;
    const p = ls.bestPerfect?.moveCount ?? null;
    const a = ls.bestAssisted?.moveCount ?? null;
    if (p === null) return a;
    if (a === null) return p;
    return Math.min(p, a);
  }

  _getBestSolutionMoveCount(levelKey: string): number | null {
    if (levelKey === 'sandbox' || levelKey.startsWith('candidate:')) {
      return null;
    }
    return this.slice.optimalMoveCounts[levelKey] ?? null;
  }

  _onShowVision({ levelKey }: { levelKey: string }) {
    const solutionData = this.dataService.getSolutionData(levelKey);
    if (!solutionData?.solutions || solutionData.solutions.length === 0) return;

    const optimalSolution = solutionData.solutions.reduce((shortest: any, current: any) =>
      current.length < shortest.length ? current : shortest
    );

    this.eventBus.emit(GameEvents.SET_VISION_VIEWED, { levelKey });
    this.eventBus.emit(GameEvents.START_SOLUTION_REPLAY, { solution: optimalSolution });
  }

  _populateOptimalMoveCounts() {
    const allIds = getAllLevelIds();
    for (const levelId of allIds) {
      const solutionData = this.dataService.getSolutionData(levelId);
      if (solutionData?.solutions && solutionData.solutions.length > 0) {
        const shortest = solutionData.solutions.reduce((s: any, c: any) =>
          c.length < s.length ? c : s
        );
        this.slice.optimalMoveCounts[levelId] = shortest.length;
      }
    }
  }
}
